import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Alert,
  AlertTitle,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid
} from '@mui/material';
import {
  Key as KeyIcon,
  Visibility,
  VisibilityOff,
  Add,
  Delete,
  Edit
} from '@mui/icons-material';
import {
  useVapiKeysQuery,
  useDeleteVapiKeyMutation,
  useCreateVapiKeyMutation,
  useUpdateVapiKeyMutation
} from '../../API/VapiKeys';
import dayjs from 'dayjs';
import { useModalDisclosure } from '@hooks/useDisclosure';

interface VapiKey {
  id: number;
  environment: string;
  privateKey: string;
  saveAppointmentKey: string;
  followUpKey: string;
  endCallKey: string;
  dndCallKey: string;
  openAiKey: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: number;
    email: string;
    name: string;
  };
  updater: {
    id: number;
    email: string;
    name: string;
  };
}

const VapiKeys = () => {
  const [showDecrypted, setShowDecrypted] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState<{ [key: number]: Set<string> }>({});
  const { isOpen: isDeleteOpen, close: closeDelete, open: openDelete, data: keyToDelete } = useModalDisclosure();
  const { isOpen: isCreateOpen, close: closeCreate, open: openCreate } = useModalDisclosure();
  const { isOpen: isEditOpen, close: closeEdit, open: openEdit, data: keyToEdit } = useModalDisclosure();

  const [createForm, setCreateForm] = useState({
    environment: 'production',
    privateKey: '',
    saveAppointmentKey: '',
    followUpKey: '',
    endCallKey: '',
    dndCallKey: '',
    openAiKey: ''
  });

  const [editForm, setEditForm] = useState({
    privateKey: '',
    saveAppointmentKey: '',
    followUpKey: '',
    endCallKey: '',
    dndCallKey: '',
    openAiKey: ''
  });

  const { data: keysData, isLoading, error, refetch } = useVapiKeysQuery({
    params: { showDecrypted },
    config: {
      keepPreviousData: true,
      onSuccess: (data) => {
        console.log('VAPI Keys data:', data);
      }
    }
  });



  const deleteKeyMutation = useDeleteVapiKeyMutation({
    onSuccess: () => {
      refetch();
      closeDelete();
    }
  });

  const createKeyMutation = useCreateVapiKeyMutation({
    onSuccess: () => {
      refetch();
      closeCreate();
      setCreateForm({
        environment: 'production',
        privateKey: '',
        saveAppointmentKey: '',
        followUpKey: '',
        endCallKey: '',
        dndCallKey: '',
        openAiKey: ''
      });
    }
  });

  const updateKeyMutation = useUpdateVapiKeyMutation({
    onSuccess: () => {
      refetch();
      closeEdit();
    }
  });

  const keys: VapiKey[] = keysData?.data || [];

  const handleToggleKeyVisibility = (keyId: number, fieldName: string) => {
    setVisibleKeys(prev => {
      const keyState = { ...prev };
      if (!keyState[keyId]) {
        keyState[keyId] = new Set();
      }
      if (keyState[keyId].has(fieldName)) {
        keyState[keyId].delete(fieldName);
      } else {
        keyState[keyId].add(fieldName);
      }
      return keyState;
    });
  };




  const handleDeleteKey = () => {
    if (keyToDelete) {
      deleteKeyMutation.mutate({ keyId: keyToDelete.environment });
    }
  };

  const handleCreateFormChange = (field: string, value: string) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateKey = () => {
    createKeyMutation.mutate(createForm);
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditKey = (key: VapiKey) => {
    setEditForm({
      privateKey: key.privateKey,
      saveAppointmentKey: key.saveAppointmentKey,
      followUpKey: key.followUpKey,
      endCallKey: key.endCallKey,
      dndCallKey: key.dndCallKey,
      openAiKey: key.openAiKey
    });
    openEdit(key);
  };

  const handleUpdateKey = () => {
    if (keyToEdit) {
      updateKeyMutation.mutate({
        environment: keyToEdit.environment,
        data: editForm
      });
    }
  };

  const maskKey = (key: string) => {
    if (!key || key.length <= 8) {
      return key;
    }
    return `${key.slice(0, 4)}${'*'.repeat(key.length - 8)}${key.slice(-4)}`;
  };

  const renderKey = (key: string, keyId: number, fieldName: string) => {
    const isVisible = showDecrypted && visibleKeys[keyId]?.has(fieldName);
    const displayKey = isVisible ? key : maskKey(key);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            backgroundColor: '#f5f5f5',
            padding: '4px 8px',
            borderRadius: 1,
            minWidth: '120px',
            fontSize: '12px',
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap'
          }}
        >
          {displayKey}
        </Typography>
        {showDecrypted && (
          <IconButton
            size="small"
            onClick={() => handleToggleKeyVisibility(keyId, fieldName)}
          >
            {isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        )}
      </Box>
    );
  };

  if (error) {
    return (
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            Failed to load VAPI keys. Please try again later.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div className="heading-dashboard" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <KeyIcon color="primary" />
            <h2>VAPI Keys Management</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showDecrypted}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = e.target.checked;
                    setShowDecrypted(newValue);
                    // The query will automatically refetch due to params change
                  }}
                  color="primary"
                />
              }
              label="Show Decrypted Keys"
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreate}
              disabled={createKeyMutation.isLoading}
            >
              Create New Key
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Paper sx={{ p: 2, flex: 1, backgroundColor: '#e3f2fd' }}>
            <Typography variant="h6" color="text.secondary">Total Keys</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {keys.length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, backgroundColor: '#e8f5e8' }}>
            <Typography variant="h6" color="text.secondary">Active Keys</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              {keys.filter(key => key.isActive).length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, backgroundColor: '#fff3e0' }}>
            <Typography variant="h6" color="text.secondary">Inactive Keys</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
              {keys.filter(key => !key.isActive).length}
            </Typography>
          </Paper>
        </Box>

        {/* Keys Table */}
        <TableContainer component={Paper}>
          {isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Loading VAPI keys...</Typography>
            </Box>
          ) : keys.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No VAPI keys found</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Environment</TableCell>
                  {/* <TableCell>Private Key</TableCell> */}
                  {/* <TableCell>Status</TableCell> */}
                  <TableCell>Created</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((vapiKey: VapiKey) => {
                  return (
                    <React.Fragment key={vapiKey.id}>
                      <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                        <TableCell>
                          <Chip
                            label={vapiKey.environment}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {dayjs(vapiKey.createdAt).format('MMM DD, YYYY')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(vapiKey.createdAt).format('HH:mm A')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{vapiKey.creator.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vapiKey.creator.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit Key">
                              <IconButton
                                size="small"
                                onClick={() => handleEditKey(vapiKey)}
                                disabled={updateKeyMutation.isLoading}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Key">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openDelete(vapiKey)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expanded details row */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>Key Details:</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Save Appointment Key:</Typography>
                                {renderKey(vapiKey.saveAppointmentKey, vapiKey.id, 'saveAppointmentKey')}
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Follow-Up Key:</Typography>
                                {renderKey(vapiKey.followUpKey, vapiKey.id, 'followUpKey')}
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">End Call Key:</Typography>
                                {renderKey(vapiKey.endCallKey, vapiKey.id, 'endCallKey')}
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">DND Call Key:</Typography>
                                {renderKey(vapiKey.dndCallKey, vapiKey.id, 'dndCallKey')}
                              </Box>
                              <Box sx={{ gridColumn: '1 / -1' }}>
                                <Typography variant="caption" color="text.secondary">OpenAI Key:</Typography>
                                <Box sx={{ width: '100%', overflow: 'auto', mt: 0.5 }}>
                                  {renderKey(vapiKey.openAiKey, vapiKey.id, 'openAiKey')}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Create Key Dialog */}
        <Dialog open={isCreateOpen} onClose={closeCreate} maxWidth="md" fullWidth>
          <DialogTitle>Create New VAPI Key</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Environment"
                  value={createForm.environment}
                  onChange={(e) => handleCreateFormChange('environment', e.target.value)}
                >
                  <MenuItem value="production">Production</MenuItem>
                  <MenuItem value="development">Development</MenuItem>
                  <MenuItem value="staging">Staging</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Private Key"
                  value={createForm.privateKey}
                  onChange={(e) => handleCreateFormChange('privateKey', e.target.value)}
                  placeholder="Enter private key"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Save Appointment Key"
                  value={createForm.saveAppointmentKey}
                  onChange={(e) => handleCreateFormChange('saveAppointmentKey', e.target.value)}
                  placeholder="Enter save appointment key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Follow-Up Key"
                  value={createForm.followUpKey}
                  onChange={(e) => handleCreateFormChange('followUpKey', e.target.value)}
                  placeholder="Enter follow-up key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Call Key"
                  value={createForm.endCallKey}
                  onChange={(e) => handleCreateFormChange('endCallKey', e.target.value)}
                  placeholder="Enter end call key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DND Call Key"
                  value={createForm.dndCallKey}
                  onChange={(e) => handleCreateFormChange('dndCallKey', e.target.value)}
                  placeholder="Enter DND call key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Open AI Key"
                  value={createForm.openAiKey}
                  onChange={(e) => handleCreateFormChange('openAiKey', e.target.value)}
                  placeholder="Enter Open AI key"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreate}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateKey}
              disabled={createKeyMutation.isLoading || !createForm.privateKey}
            >
              {createKeyMutation.isLoading ? 'Creating...' : 'Create Key'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Key Dialog */}
        <Dialog open={isEditOpen} onClose={closeEdit} maxWidth="md" fullWidth>
          <DialogTitle>Edit VAPI Key - {keyToEdit?.environment}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Private Key"
                  value={editForm.privateKey}
                  onChange={(e) => handleEditFormChange('privateKey', e.target.value)}
                  placeholder="Enter private key"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Save Appointment Key"
                  value={editForm.saveAppointmentKey}
                  onChange={(e) => handleEditFormChange('saveAppointmentKey', e.target.value)}
                  placeholder="Enter save appointment key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Follow-Up Key"
                  value={editForm.followUpKey}
                  onChange={(e) => handleEditFormChange('followUpKey', e.target.value)}
                  placeholder="Enter follow-up key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Call Key"
                  value={editForm.endCallKey}
                  onChange={(e) => handleEditFormChange('endCallKey', e.target.value)}
                  placeholder="Enter end call key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DND Call Key"
                  value={editForm.dndCallKey}
                  onChange={(e) => handleEditFormChange('dndCallKey', e.target.value)}
                  placeholder="Enter DND call key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Open AI Key"
                  value={editForm.openAiKey}
                  onChange={(e) => handleEditFormChange('openAiKey', e.target.value)}
                  placeholder="Enter Open AI key"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEdit}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleUpdateKey}
              disabled={updateKeyMutation.isLoading || !editForm.privateKey}
            >
              {updateKeyMutation.isLoading ? 'Updating...' : 'Update Key'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteOpen} onClose={closeDelete}>
          <DialogTitle>Delete VAPI Key</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Warning</AlertTitle>
              This action cannot be undone. Deleting this key will immediately revoke access for any applications using it.
            </Alert>
            <Typography>
              Are you sure you want to delete the VAPI key for <strong>{keyToDelete?.environment}</strong> environment?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDelete}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteKey}
              disabled={deleteKeyMutation.isLoading}
            >
              Delete Key
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default VapiKeys;